import { useCallback, useRef, useState } from "react";

/**
 * useApiMutation – manages a single async mutation (POST / PATCH / DELETE).
 *
 * Features:
 *  - Tracks `data`, `error`, and `isLoading` for the most recent call.
 *  - Supports request cancellation via AbortController.
 *  - Stale-request guard: only the latest in-flight request updates state.
 *
 * @param {(vars: any, ctx: { signal: AbortSignal }) => Promise<any>} mutationFn
 *   The async function that performs the API call.
 * @returns {{ data, error, setError, isLoading, mutate, reset, cancel, setData }}
 */
export function useApiMutation(mutationFn) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Abort controller ref – allows cancelling the in-flight request
  const abortRef = useRef(null);
  // Monotonically increasing request id – prevents stale responses from updating state
  const reqIdRef = useRef(0);

  /** Reset all state and abort any pending request */
  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    reqIdRef.current += 1;
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  /** Abort the current request without clearing state */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /**
   * mutate – trigger the mutation.
   * @param {any} vars - payload forwarded to mutationFn.
   * @param {{ cancelPrevious?: boolean }} opts
   * @returns {Promise<any>} the raw API response on success.
   */
  const mutate = useCallback(
    async (vars, { cancelPrevious = false } = {}) => {
      // Optionally cancel a previous in-flight request
      if (cancelPrevious) abortRef.current?.abort();

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const reqId = ++reqIdRef.current;

      setIsLoading(true);
      setError(null);

      try {
        const res = await mutationFn(vars, { signal: ctrl.signal });
        // Only commit if this is still the latest request
        if (reqId === reqIdRef.current && res.status >= 200) {
          setError(null);
          setData(res);
        }
        return res;
      } catch (err) {
        console.error("HTTP error: ", err);
        // Ignore AbortError – it's expected when we cancel on purpose
        if (err?.name !== "AbortError" && reqId === reqIdRef.current) {
          setError(err);
        }
      } finally {
        if (reqId === reqIdRef.current) setIsLoading(false);
      }
    },
    [mutationFn]
  );

  return { data, error, setError, isLoading, mutate, reset, cancel, setData };
}
